import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { BookOpen, Users, Briefcase, Music, ArrowRight, CheckCircle2, Calendar, Recycle, X, Loader2, GraduationCap, Award, Sparkles, Building2, Handshake, UserPlus, PartyPopper, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { saveSubmission } from '../services/databaseService';
import { sendViaWeb3Forms } from '../services/web3formsService';
type ModalType = 'ateliers' | 'employeur' | 'partenariat' | 'recyclage' | 'newsletter' | null;
export function ActionsPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formationAutre, setFormationAutre] = useState('');
  const [showFormationAutre, setShowFormationAutre] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  // Auto-open ateliers modal if URL contains #inscription
  useEffect(() => {
    if (window.location.hash === '#inscription') {
      setActiveModal('ateliers');
      // Clean the hash without page reload
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(formRef.current!);
    const data: Record<string, any> = {};
    formData.forEach((value, key) => {
      if (data[key]) {
        // Handle multiple values (checkboxes)
        if (Array.isArray(data[key])) {
          data[key].push(value);
        } else {
          data[key] = [data[key], value];
        }
      } else {
        data[key] = value;
      }
    });
    // Map form data to submission types
    let submission: any = {
      type: ''
    };
    if (activeModal === 'ateliers') {
      submission = {
        type: 'atelier',
        nom: data.nom || '',
        prenom: data.prenom || '',
        telephone: data.telephone || '',
        email: data.email || '',
        ville: data.ville || '',
        quartier: data.quartier || '',
        sexe: data.sexe || '',
        dateNaissance: data.dateNaissance || '',
        situation: data.situation || '',
        niveauEtudes: data.niveauEtudes || '',
        formationSouhaitee: data.formationSouhaitee || '',
        formationAutre: data.formationAutre || '',
        source: data.source || ''
      };
    } else if (activeModal === 'employeur') {
      submission = {
        type: 'employeur',
        nomEmployeur: data.nomEmployeur || '',
        typeEmployeur: data.typeEmployeur || '',
        nomContact: data.nomContact || '',
        telephone: data.telephone || '',
        email: data.email || '',
        ville: data.ville || '',
        profilsRecherches: data.profilsRecherches || '',
        attentes: data.attentes || ''
      };
    } else if (activeModal === 'recyclage') {
      submission = {
        type: 'recyclage',
        nomEmployeur: data.nomEmployeur || '',
        nomEmploye: data.nomEmploye || '',
        posteActuel: data.posteActuel || '',
        anciennete: data.anciennete || '',
        telephoneEmploye: data.telephoneEmploye || '',
        pointsFormation: Array.isArray(data.pointsFormation) ? data.pointsFormation.join(', ') : data.pointsFormation || '',
        autresPoints: data.autresPoints || '',
        periodeSouhaitee: data.periodeSouhaitee || '',
        dureeSouhaitee: data.dureeSouhaitee || '',
        observations: data.observations || ''
      };
    }
    setTimeout(async () => {
      try {
        saveSubmission(submission);
        // Envoyer via Web3Forms
        const formData2 = new FormData(formRef.current!);
        const fields: Record<string, string> = {};
        formData2.forEach((value, key) => {
          if (typeof value === 'string') {
            fields[key] = value;
          } else if (Array.isArray(value)) {
            fields[key] = value.join(', ');
          }
        });
        // Gérer les checkboxes multiples pour le recyclage
        if (activeModal === 'recyclage') {
          const pointsFormation = formData2.getAll('pointsFormation');
          if (pointsFormation.length > 0) {
            fields['pointsFormation'] = pointsFormation.join(', ');
          }
        }
        const web3Result = await sendViaWeb3Forms({
          type: activeModal!,
          fields
        });
        console.log(`📋 ${activeModal} submission result:`, web3Result);
        setIsSubmitting(false);
        setIsSuccess(true);
      } catch (error) {
        console.error('Error saving submission:', error);
        setIsSubmitting(false);
        // Show success anyway to not block user
        setIsSuccess(true);
      }
    }, 1000);
  };
  const closeModal = () => {
    setActiveModal(null);
    setIsSuccess(false);
    setShowFormationAutre(false);
    setFormationAutre('');
  };
  const inputClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all';
  const selectClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all bg-white';
  const labelClass = 'text-sm font-medium text-gray-700';
  const modalConfigs = {
    ateliers: {
      title: 'Inscription aux Ateliers',
      subtitle: 'Remplissez ce formulaire pour participer à nos prochaines sessions de formation.',
      successMessage: "Votre demande d'inscription a été enregistrée. Notre équipe vous contactera pour valider votre participation.",
      icon: GraduationCap
    },
    employeur: {
      title: 'Devenir Employeur Partenaire',
      subtitle: "Recrutez du personnel qualifié et formé par l'ADCC.",
      successMessage: 'Votre demande de partenariat a été reçue. Notre équipe vous contactera dans les plus brefs délais.',
      icon: Building2
    },
    partenariat: {
      title: 'Devenir Partenaire',
      subtitle: "Associez votre image à l'ADCC et soutenez l'insertion professionnelle",
      successMessage: 'Proposition de partenariat reçue ! Notre équipe vous contactera pour en discuter.',
      icon: Handshake
    },
    recyclage: {
      title: 'Demande de Recyclage',
      subtitle: 'Mettez à jour vos compétences avec nos modules de formation continue.',
      successMessage: 'Votre demande de recyclage a été enregistrée. Nous vous contacterons pour planifier votre session.',
      icon: RefreshCw
    }
  };
  return <Layout data-id="element-213" data-mp="7y8ctvkhg4v">
      <SEO {...pageSEO.actions} data-id="element-214" data-mp="38pkvwp4fbdug" />
      {/* Header */}
      <div className="bg-[#2C3E50] text-white py-16 md:py-24" data-id="element-215" data-mp="38o1gf0qld6tq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-216" data-mp="34oix29ef53t0">
          <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1 text-sm font-medium text-blue-200 mb-4" data-id="element-217" data-mp="2uk2jjmoevysd">
            Nos Actions
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-218" data-mp="2klwi166iouo1">
            Agir pour l'autonomisation
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-219" data-mp="2pkg3z6l426ge">
            Découvrez nos programmes de formation, d'accompagnement et
            d'insertion professionnelle au Bénin.
          </p>
        </div>
      </div>

      {/* Four Pillars Introduction */}
      <section className="py-16 bg-white" data-id="element-220" data-mp="33ce5av8kyy20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-221" data-mp="33bcm23hev597">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-222" data-mp="38b7a7z9sxgqk">
            {[{
            icon: BookOpen,
            title: 'Développement des Compétences',
            desc: 'Acquisition de compétences techniques'
          }, {
            icon: Users,
            title: 'Valorisation Sociale',
            desc: 'Reconnaissance et dignité des métiers de service'
          }, {
            icon: Briefcase,
            title: 'Insertion & Suivi',
            desc: 'Accompagnement vers un emploi durable'
          }, {
            icon: Recycle,
            title: 'Recyclage',
            desc: 'Reconversion et mise à jour des compétences'
          }].map((pillar, index) => <Card key={index} className="text-center border-t-4 border-t-[#1E88E5] hover:shadow-lg transition-shadow duration-300" data-id="element-223" data-mp="1rxpksowhb9m0" data-mpi={index}>
                <CardContent className="pt-8 pb-6" data-id="element-224" data-mp="1j3t4vrtwqtf1">
                  <div className="inline-flex items-center justify-center p-4 bg-blue-50 rounded-full mb-4" data-id="element-225" data-mp="2fmqmzuepxoiu">
                    <pillar.icon className="w-8 h-8 text-[#1E88E5]" data-id="element-226" data-mp="2awab4eqjqayl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-227" data-mp="3tis1m9d27q5e">
                    {pillar.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-228" data-mp="2bpatj14vlvp">{pillar.desc}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Main Programs - Restructured */}
      <div className="bg-gray-50" data-id="element-229" data-mp="bdyim150p15e">
        {/* Insertion & Suivi Section */}
        <section className="py-20 bg-white" data-id="element-230" data-mp="gd1o1mowt9dw">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-231" data-mp="gc038qi3d8iz">
            <div className="text-center mb-16" data-id="element-232" data-mp="labbwxwnhczm">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm mb-2 block" data-id="element-233" data-mp="l907qwzam37z">
                AUTRE PILIER STRATÉGIQUE
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-234" data-mp="u355m0cugex0">
                Insertion & Suivi Professionnels
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-235" data-mp="141kvk004xi4x">
                L'ADCC met en place un dispositif d'accompagnement continu de
                personnels qui ont suivi nos ateliers, en collaboration avec des
                familles, entreprises, institutions et partenaires.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-236" data-mp="2c0jte7rydijp">
              {/* Left Column - Ce que nous proposons */}
              <Card className="border border-gray-100 shadow-sm" data-id="element-237" data-mp="2gtmh3za1txrz">
                <CardContent className="p-8" data-id="element-238" data-mp="2lqcwslhxyrcv">
                  <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-239" data-mp="2ltsb3m3sjwd8">
                    Ce que nous proposons
                  </h3>
                  <div className="space-y-5" data-id="element-240" data-mp="2vpc5u56mcp0e">
                    {[{
                    title: 'Identification des besoins et préparation des profils',
                    desc: 'Évaluation des compétences et matching avec les besoins'
                  }, {
                    title: 'Mise en relation avec des employeurs',
                    desc: 'Réseau de familles, entreprises et structures partenaires'
                  }, {
                    title: "Sensibilisation aux bonnes pratiques d'emploi",
                    desc: 'Accompagnement des employeurs au respect des droits'
                  }, {
                    title: 'Suivi post-placement et médiation si nécessaire',
                    desc: 'Accompagnement continu pour garantir la durabilité'
                  }].map((item, idx) => <div key={idx} className="flex gap-3" data-id="element-241" data-mp="17q9rtr87sqiq" data-mpi={idx}>
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-400 flex-shrink-0 mt-2" data-id="element-242" data-mp="12t9nbkr0jvtq" />
                        <div data-id="element-243" data-mp="3sildu2rwcpt4">
                          <h4 className="font-bold text-gray-900" data-id="element-244" data-mp="3sjmz102mmsok">
                            {item.title}
                          </h4>
                          <p className="text-sm text-gray-500" data-id="element-245" data-mp="3p04o7wpw2eel">{item.desc}</p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>

              {/* Right Column - Notre Objectif */}
              <Card className="bg-[#1E88E5] text-white border-none shadow-lg flex flex-col" data-id="element-246" data-mp="3g1ixvfe6dv39">
                <CardContent className="p-8 flex-grow flex flex-col" data-id="element-247" data-mp="3jz8bqurh1jxk">
                  <h3 className="text-xl font-bold mb-4" data-id="element-248" data-mp="368v12kn73r4j">Notre Objectif</h3>
                  <p className="text-blue-100 mb-8" data-id="element-249" data-mp="3b0v9l1hbzscq">
                    Créer un cadre structuré, sécurisé et respectueux pour
                    l'emploi des personnels de maison et de services.
                  </p>

                  <div className="space-y-5 mb-8 flex-grow" data-id="element-250" data-mp="31blc163pxhh8">
                    <div className="flex items-center gap-4" data-id="element-251" data-mp="2nb0wqjx0i3sh">
                      <div className="w-3 h-3 rounded-full bg-green-400 flex-shrink-0" data-id="element-252" data-mp="2nbkcpv33qi3v" />
                      <div data-id="element-253" data-mp="2s9c84z2ygmct">
                        <h4 className="font-bold text-green-300" data-id="element-254" data-mp="2sbok8vm182hy">Dignité</h4>
                        <p className="text-sm text-blue-100" data-id="element-255" data-mp="28obwugyulolk">
                          Respect des droits et de la personne
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-256" data-mp="2hmnlidg5mcbd">
                      <div className="w-3 h-3 rounded-full bg-blue-300 flex-shrink-0" data-id="element-257" data-mp="2dm3nl7nrb4o6" />
                      <div data-id="element-258" data-mp="2rm64oro8pu2d">
                        <h4 className="font-bold text-blue-200" data-id="element-259" data-mp="2rld468fvk0so">
                          Transparence
                        </h4>
                        <p className="text-sm text-blue-100" data-id="element-260" data-mp="2mm0gxml3crpk">
                          Clarté sur les conditions d'emploi
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-261" data-mp="3adfdeyq6sesw">
                      <div className="w-3 h-3 rounded-full bg-amber-400 flex-shrink-0" data-id="element-262" data-mp="3adyuyebxd16e" />
                      <div data-id="element-263" data-mp="35fnl43lmqqo6">
                        <h4 className="font-bold text-amber-300" data-id="element-264" data-mp="35c9lh34covkf">Durabilité</h4>
                        <p className="text-sm text-blue-100" data-id="element-265" data-mp="13onf4rlnbcxk">
                          Insertion professionnelle à long terme
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mt-auto" data-id="element-266" data-mp="1dhchmwz7xggr">
                    <Button onClick={() => setActiveModal('employeur')} variant="outline" className="w-full border-white text-white hover:bg-white/10 hover:text-white gap-2" data-id="element-267" data-mp="1hfa5nwh95wik">
                      <Building2 className="w-4 h-4" data-id="element-268" data-mp="tr9457ihrgcn" />
                      Devenir employeur partenaire
                    </Button>
                    <Button onClick={() => setActiveModal('recyclage')} variant="outline" className="w-full border-white text-white hover:bg-white/10 hover:text-white gap-2" data-id="element-269" data-mp="yqvffberemit">
                      <RefreshCw className="w-4 h-4" data-id="element-270" data-mp="2k8wece7lg3n5" />
                      Recycler mon employé
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Engagement Banner */}
        <section className="py-12 bg-gray-50" data-id="element-271" data-mp="26hraezx70p70">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-272" data-mp="26gpvulgyxkkj">
            <div className="bg-gradient-to-r from-[#1E88E5] to-[#1565C0] rounded-2xl p-10 text-center text-white shadow-xl" data-id="element-273" data-mp="21ielm9mrh81s">
              <h3 className="text-2xl md:text-3xl font-bold italic mb-4" data-id="element-274" data-mp="1wijwobmjqsjd">
                "Notre engagement : transformer les compétences en opportunités
                d'emploi."
              </h3>
              <p className="text-blue-100" data-id="element-275" data-mp="1njyieso3xzoe">
                L'insertion professionnelle constitue un indicateur clé de
                réussite de nos programmes.
              </p>
            </div>
          </div>
        </section>

        {/* Partenaires CTA */}
        <section className="py-20 bg-white" data-id="element-276" data-mp="1faj16qvnv3rb">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-277" data-mp="1f9hetq972uua">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-278" data-mp="1k198wark7ip6">
              Rejoignez notre réseau de partenaires
            </h2>
            <p className="text-lg text-gray-600 mb-10" data-id="element-279" data-mp="16bffdf540a94">
              Que vous soyez une famille, une entreprise ou une institution,
              devenez partenaire de l'ADCC pour favoriser l'insertion
              professionnelle digne.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4" data-id="element-280" data-mp="2303kjavq8314">
              <Button onClick={() => setActiveModal('employeur')} size="lg" className="gap-2 bg-[#1E88E5] hover:bg-[#1976D2]" data-id="element-281" data-mp="1p0lrukfb85mh">
                <UserPlus className="w-5 h-5" data-id="element-282" data-mp="dl6w1jyu7jsy" />
                Recruter un personnel compétent
              </Button>
              <Button onClick={() => window.location.href = '/partner'} variant="outline" size="lg" className="gap-2 bg-[#2C3E50] text-white hover:bg-[#1a252f] border-none" data-id="element-283" data-mp="ika1h5iqbs1g">
                <Handshake className="w-5 h-5" data-id="element-284" data-mp="63h6frb74eaf" />
                Proposer un partenariat
              </Button>
              <Button onClick={() => setActiveModal('recyclage')} variant="outline" size="lg" className="gap-2 bg-[#2C3E50] text-white hover:bg-[#1a252f] border-none" data-id="element-285" data-mp="f22ug1zfslil">
                <RefreshCw className="w-5 h-5" data-id="element-286" data-mp="3ik47qqnoaog5" />
                Recycler mon employé
              </Button>
            </div>
          </div>
        </section>

        {/* Droit à l'image */}
        <section className="py-8 pb-16" data-id="element-287" data-mp="3sfxtn0na7ual">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-288" data-mp="3sew6hxszrhci">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6" data-id="element-289" data-mp="34q3h6k6vk7qv">
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-290" data-mp="2zt3ac727b0yp">
                <span className="font-semibold text-gray-900" data-id="element-291" data-mp="2e907jozx9vuq">
                  📷 Droit à l'image :
                </span>{' '}
                En participant aux activités ADCC (formations, ateliers, foires,
                événements), vous autorisez ADCC à utiliser votre image (photos
                et vidéos) réalisées lors de ces activités à des fins de
                communication institutionnelle et promotionnelle sur le site
                adcc.africa et les supports numériques et imprimés associés.
                Cette autorisation peut être retirée à tout moment par demande
                écrite adressée à ADCC, sans effet rétroactif sur les contenus
                déjà publiés.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-id="element-292" data-mp="3gwmxyt689yqj">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8 relative max-h-[90vh] overflow-y-auto" data-id="element-293" data-mp="3kxyl8s1mq7vk">
            <button onClick={closeModal} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors z-10" data-id="element-294" data-mp="197z9q6c6fem6">
              <X className="w-6 h-6" data-id="element-295" data-mp="10bhtiooygqjk" />
            </button>

            <div className="p-8" data-id="element-296" data-mp="vbcaty0nyedm">
              <div className="text-center mb-8" data-id="element-297" data-mp="vc5bch9147nb">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E88E5] rounded-full mb-4" data-id="element-298" data-mp="1syksfhxjn3ac">
                  {activeModal === 'ateliers' && <GraduationCap className="w-8 h-8 text-white" data-id="element-299" data-mp="1o1c8bbsu4p6g" />}
                  {activeModal === 'employeur' && <Building2 className="w-8 h-8 text-white" data-id="element-300" data-mp="1e853pt5x5d7f" />}
                  {activeModal === 'recyclage' && <RefreshCw className="w-8 h-8 text-white" data-id="element-301" data-mp="2bpvhdtjpl5zl" />}
                </div>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-302" data-mp="26vr7b1f5chua">
                  {/* @ts-ignore */}
                  {modalConfigs[activeModal]?.title}
                </h2>
                <p className="text-gray-600 mt-2" data-id="element-303" data-mp="2jkeldiqfj2g6">
                  {/* @ts-ignore */}
                  {modalConfigs[activeModal]?.subtitle}
                </p>
              </div>

              {isSuccess ? <div className="text-center py-12" data-id="element-304" data-mp="2an4cg4133pef">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-305" data-mp="37btqfj9lbxv4">
                    <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-306" data-mp="32ds9p4zn0amt" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-307" data-mp="2xkpfcun1cx5k">
                    {activeModal === 'ateliers' && 'Inscription enregistrée !'}
                    {activeModal === 'employeur' && 'Demande envoyée !'}
                    {activeModal === 'recyclage' && 'Demande enregistrée !'}
                  </h3>
                  <p className="text-gray-600 mb-8" data-id="element-308" data-mp="3la1wcgolu9if">
                    {/* @ts-ignore */}
                    {modalConfigs[activeModal]?.successMessage}
                  </p>
                  <Button onClick={closeModal} data-id="element-309" data-mp="1csl792b2rgfp">Fermer</Button>
                </div> : <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-310" data-mp="1gslrj3kuuluo">
                  {/* Formulaire Ateliers */}
                  {activeModal === 'ateliers' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-311" data-mp="1vqeak5pm1sdm">
                      <div className="space-y-2" data-id="element-312" data-mp="1vob82m79u6s0">
                        <label className={labelClass} data-id="element-313" data-mp="20ifijffqwwxu">Nom complet *</label>
                        <input name="nom" type="text" required className={inputClass} placeholder="Votre nom de famille" data-id="element-314" data-mp="24jhijyhhld0v" />
                      </div>
                      <div className="space-y-2" data-id="element-315" data-mp="eecxujjp1m2y">
                        <label className={labelClass} data-id="element-316" data-mp="ef5ugroxuv7d">Prénom *</label>
                        <input name="prenom" type="text" required className={inputClass} placeholder="Votre prénom" data-id="element-317" data-mp="9guo4qxur2tw" />
                      </div>
                      <div className="space-y-2" data-id="element-318" data-mp="x4ceci1gxsur">
                        <label className={labelClass} data-id="element-319" data-mp="t3sl3pk0n9dw">Email *</label>
                        <input name="email" type="email" required className={inputClass} placeholder="votre@email.com" data-id="element-320" data-mp="t3itjxjbou63" />
                      </div>
                      <div className="space-y-2" data-id="element-321" data-mp="38v6q3rufckod">
                        <label className={labelClass} data-id="element-322" data-mp="33wd8poc0o9ix">Téléphone *</label>
                        <input name="telephone" type="tel" required className={inputClass} placeholder="+229..." data-id="element-323" data-mp="33y6ez2ipwrqe" />
                      </div>
                      <div className="space-y-2" data-id="element-324" data-mp="19m5cejfydhfv">
                        <label className={labelClass} data-id="element-325" data-mp="1em1a60y24cmz">Ville / Commune *</label>
                        <input name="ville" type="text" required className={inputClass} placeholder="Ex: Cotonou" data-id="element-326" data-mp="10ms07j0g2erg" />
                      </div>
                      <div className="space-y-2" data-id="element-327" data-mp="vnoxi57xskmn">
                        <label className={labelClass} data-id="element-328" data-mp="vogqd8a5g498">Quartier *</label>
                        <input name="quartier" type="text" required className={inputClass} placeholder="Ex: Haie Vive" data-id="element-329" data-mp="2l4g8hevqx410" />
                      </div>
                      <div className="space-y-2" data-id="element-330" data-mp="lzpwd0yumt0b">
                        <label className={labelClass} data-id="element-331" data-mp="gzbn5flihd0p">
                          Date de naissance *
                        </label>
                        <input name="dateNaissance" type="date" required className={inputClass} data-id="element-332" data-mp="bz5rloc4f6dc" />
                      </div>
                      <div className="space-y-2" data-id="element-333" data-mp="28kuk5c3i9wj">
                        <label className={labelClass} data-id="element-334" data-mp="3uf9mhr483vb5">Sexe *</label>
                        <select name="sexe" required className={selectClass} data-id="element-335" data-mp="3pfdjnv79i9x6">
                          <option value="" data-id="element-336" data-mp="3pdajiiceb0eq">Sélectionnez...</option>
                          <option value="Féminin" data-id="element-337" data-mp="3kfirzpfnxgb2">Féminin</option>
                          <option value="Masculin" data-id="element-338" data-mp="3fgnyntk2k9cp">Masculin</option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-339" data-mp="35c7n3cdmheep">
                        <label className={labelClass} data-id="element-340" data-mp="3abkgkfz01jq9">
                          Situation actuelle *
                        </label>
                        <select name="situation" required className={selectClass} data-id="element-341" data-mp="3fchv8iksvvn0">
                          <option value="" data-id="element-342" data-mp="2mnkg2obvyeyh">Sélectionnez...</option>
                          <option value="Étudiant(e)" data-id="element-343" data-mp="2rlciica5pjgx">Étudiant(e)</option>
                          <option value="En recherche d'emploi" data-id="element-344" data-mp="2rj9jj2r10g02">
                            En recherche d'emploi
                          </option>
                          <option value="En emploi" data-id="element-345" data-mp="31cgxvd3qv6ca">En emploi</option>
                          <option value="Autre" data-id="element-346" data-mp="23w9ridtmue9r">Autre</option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-347" data-mp="28ub90ubeu5j4">
                        <label className={labelClass} data-id="element-348" data-mp="20uf2m5ogx3ev">Niveau d'études *</label>
                        <select name="niveauEtudes" required className={selectClass} data-id="element-349" data-mp="20sc2gstlptwf">
                          <option value="" data-id="element-350" data-mp="2ajghfsja1er9">Sélectionnez...</option>
                          <option value="Aucun" data-id="element-351" data-mp="25kvidwfrh14e">Aucun</option>
                          <option value="Primaire" data-id="element-352" data-mp="1hvknuwp082yp">Primaire</option>
                          <option value="Secondaire" data-id="element-353" data-mp="1hvt14pp1haaf">Secondaire</option>
                          <option value="Supérieur" data-id="element-354" data-mp="1cxj387bxjvl4">Supérieur</option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-355" data-mp="1rv1srbwfmctm">
                        <label className={labelClass} data-id="element-356" data-mp="1mtm5dd6s7u82">
                          Formation souhaitée *
                        </label>
                        <select name="formationSouhaitee" required className={selectClass} onChange={e => setShowFormationAutre(e.target.value === 'Autre')} data-id="element-357" data-mp="3372lt30bwnf6">
                          <option value="" data-id="element-358" data-mp="2wt5cf1o42nf0">Sélectionnez...</option>
                          <option value="Personnel de maison" data-id="element-359" data-mp="2wuzzommuurm9">
                            Personnel de maison (Nounou, Cuisinier...)
                          </option>
                          <option value="Hôtellerie / Restauration" data-id="element-360" data-mp="3okcvvvjckj78">
                            Hôtellerie / Restauration
                          </option>
                          <option value="Aide à la personne" data-id="element-361" data-mp="3frrhssqnv0oq">
                            Aide à la personne
                          </option>
                          <option value="Autre" data-id="element-362" data-mp="3jrjspc3j1ix9">Autre</option>
                        </select>
                      </div>
                      {showFormationAutre && <div className="space-y-2 md:col-span-2" data-id="element-363" data-mp="g49m9k8ockg9">
                          <label className={labelClass} data-id="element-364" data-mp="3nirg1c5kr780">
                            Précisez la formation souhaitée *
                          </label>
                          <input name="formationAutre" type="text" required className={inputClass} placeholder="Quelle formation ?" data-id="element-365" data-mp="3ir9hsvuxrkqq" />
                        </div>}
                      <div className="space-y-2 md:col-span-2" data-id="element-366" data-mp="5degnrnj61w3">
                        <label className={labelClass} data-id="element-367" data-mp="5dwjj74p7ecz">
                          Comment avez-vous connu l'ADCC ? *
                        </label>
                        <select name="source" required className={selectClass} data-id="element-368" data-mp="3rkuwkf73p205">
                          <option value="" data-id="element-369" data-mp="2l49e975x076s">Sélectionnez...</option>
                          <option value="Réseaux sociaux" data-id="element-370" data-mp="2l5kj7ab3jkzh">
                            Réseaux sociaux
                          </option>
                          <option value="Bouche à oreille" data-id="element-371" data-mp="vnf9jf9qn7d9">
                            Bouche à oreille
                          </option>
                          <option value="Radio / TV" data-id="element-372" data-mp="10n099rwqfjr1">Radio / TV</option>
                          <option value="Autre" data-id="element-373" data-mp="15ktf2ju4ibqs">Autre</option>
                        </select>
                      </div>
                    </div>}

                  {/* Formulaire Employeur */}
                  {activeModal === 'employeur' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-374" data-mp="1fdh58vqllhgj">
                      <div className="space-y-2 md:col-span-2" data-id="element-375" data-mp="1jawtiomrh15o">
                        <label className={labelClass} data-id="element-376" data-mp="1ocuj00lr8s72">
                          Nom de l'entreprise / Famille *
                        </label>
                        <input name="nomEmployeur" type="text" required className={inputClass} placeholder="Ex: Hôtel Azalaï" data-id="element-377" data-mp="1t8tb9za7eucr" />
                      </div>
                      <div className="space-y-2" data-id="element-378" data-mp="3gm7tiwnoklp5">
                        <label className={labelClass} data-id="element-379" data-mp="3lfu6kpxrarlh">Nom du contact *</label>
                        <input name="nomContact" type="text" required className={inputClass} placeholder="Votre nom" data-id="element-380" data-mp="36qejbjxfp4aj" />
                      </div>
                      <div className="space-y-2" data-id="element-381" data-mp="88sfvcu9tqq7">
                        <label className={labelClass} data-id="element-382" data-mp="866c7oai3vd9">Type d'employeur *</label>
                        <select name="typeEmployeur" required className={selectClass} data-id="element-383" data-mp="3pfunch8bvevp">
                          <option value="" data-id="element-384" data-mp="2e0frjgruut26">Sélectionnez...</option>
                          <option value="Famille (particulier)" data-id="element-385" data-mp="2j0toa2o0agkw">
                            Famille (particulier)
                          </option>
                          <option value="Entreprise" data-id="element-386" data-mp="110udnlvmzk9i">Entreprise</option>
                          <option value="Institution / ONG" data-id="element-387" data-mp="w2j4zeh2vfsv">
                            Institution / ONG
                          </option>
                        </select>
                      </div>
                      <div className="space-y-2" data-id="element-388" data-mp="162tiz1axsr93">
                        <label className={labelClass} data-id="element-389" data-mp="15zxfyyk8fcds">Email *</label>
                        <input name="email" type="email" required className={inputClass} placeholder="votre@email.com" data-id="element-390" data-mp="icphazhb71kq" />
                      </div>
                      <div className="space-y-2" data-id="element-391" data-mp="mchpvc6pd7q3">
                        <label className={labelClass} data-id="element-392" data-mp="3ut2qa4l9qshl">Téléphone *</label>
                        <input name="telephone" type="tel" required className={inputClass} placeholder="+229..." data-id="element-393" data-mp="3ur88gz5anghq" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-394" data-mp="1yo3h1ji6p07p">
                        <label className={labelClass} data-id="element-395" data-mp="23hogzh3c849j">Ville / Commune *</label>
                        <input name="ville" type="text" required className={inputClass} placeholder="Ex: Cotonou" data-id="element-396" data-mp="28f7tabgh66yu" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-397" data-mp="1kqf2suimgrbm">
                        <label className={labelClass} data-id="element-398" data-mp="1pprt5p8pcgiy">
                          Profils recherchés *
                        </label>
                        <input name="profilsRecherches" type="text" required className={inputClass} placeholder="Ex: Cuisinier, Réceptionniste..." data-id="element-399" data-mp="2w4sdsysa1ca8" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-400" data-mp="kja26c3bfsx6">
                        <label className={labelClass} data-id="element-401" data-mp="fr0428s1rmjt">Vos attentes *</label>
                        <textarea name="attentes" required rows={3} className={inputClass} placeholder="Qu'attendez-vous de l'ADCC ?" data-id="element-402" data-mp="asyklmqpll7t"></textarea>
                      </div>
                    </div>}

                  {/* Formulaire Recyclage */}
                  {activeModal === 'recyclage' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-403" data-mp="3o1bd4u42xjrk">
                      <div className="space-y-2 md:col-span-2" data-id="element-404" data-mp="1r6f2lgzwmmh1">
                        <label className={labelClass} data-id="element-405" data-mp="1m7kj0cu44tvv">
                          Nom de l'entreprise / Famille *
                        </label>
                        <input name="nomEmployeur" type="text" required className={inputClass} placeholder="Ex: Hôtel Azalaï" data-id="element-406" data-mp="jv7m47e4hy7h" />
                      </div>
                      <div className="space-y-2" data-id="element-407" data-mp="exfp4yymflwf">
                        <label className={labelClass} data-id="element-408" data-mp="5dx212slx6y">
                          Nom de l'employé(e) *
                        </label>
                        <input name="nomEmploye" type="text" required className={inputClass} placeholder="Nom de la personne à former" data-id="element-409" data-mp="4yghnjpldwb0" />
                      </div>
                      <div className="space-y-2" data-id="element-410" data-mp="3dfbas7gl82bw">
                        <label className={labelClass} data-id="element-411" data-mp="3ib0d2ijzrxbw">Poste actuel *</label>
                        <input name="posteActuel" type="text" required className={inputClass} placeholder="Ex: Cuisinier" data-id="element-412" data-mp="2ypqfamu1tajp" />
                      </div>
                      <div className="space-y-2" data-id="element-413" data-mp="33p3bvzaq1vzh">
                        <label className={labelClass} data-id="element-414" data-mp="33rfqc2h9to7s">Ancienneté *</label>
                        <input name="anciennete" type="text" required className={inputClass} placeholder="Ex: 2 ans" data-id="element-415" data-mp="3hnt719dzdll7" />
                      </div>
                      <div className="space-y-2" data-id="element-416" data-mp="3mh5ngw8iam2v">
                        <label className={labelClass} data-id="element-417" data-mp="57h9lvjaoogq">
                          Téléphone de l'employé(e) *
                        </label>
                        <input name="telephoneEmploye" type="tel" required className={inputClass} placeholder="+229..." data-id="element-418" data-mp="9xj9x9bol8f" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-419" data-mp="h98jnvbn4g1x">
                        <label className={labelClass} data-id="element-420" data-mp="m18oo26khz5d">
                          Points à améliorer *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2" data-id="element-421" data-mp="qz1swpob8j30">
                          {['Hygiène et propreté', 'Service client / Accueil', 'Cuisine / Restauration', 'Gestion du temps', 'Savoir-être professionnel', 'Sécurité au travail'].map(point => <label key={point} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-422" data-mp="36q67j39rhl79">
                              <input type="checkbox" name="pointsFormation" value={point} className="w-4 h-4 text-[#1E88E5] border-gray-300 rounded focus:ring-[#1E88E5]" data-id="element-423" data-mp="2uff65bh7mw9d" />
                              <span className="text-sm text-gray-700" data-id="element-424" data-mp="3d77zx843sli5">
                                {point}
                              </span>
                            </label>)}
                        </div>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-425" data-mp="338ij7iny173h">
                        <label className={labelClass} data-id="element-426" data-mp="1wt8160gzv1wh">
                          Autres points à aborder (Optionnel)
                        </label>
                        <textarea name="autresPoints" rows={2} className={inputClass} placeholder="Précisez d'autres besoins spécifiques..." data-id="element-427" data-mp="1rvzk637l13wt"></textarea>
                      </div>
                      <div className="space-y-2" data-id="element-428" data-mp="28joy5wsjfn8d">
                        <label className={labelClass} data-id="element-429" data-mp="2cjz0mzh4fvih">
                          Période souhaitée *
                        </label>
                        <input name="periodeSouhaitee" type="text" required className={inputClass} placeholder="Ex: Mois prochain" data-id="element-430" data-mp="30al9uu2v3lu2" />
                      </div>
                      <div className="space-y-2" data-id="element-431" data-mp="2vfffd6ve8yud">
                        <label className={labelClass} data-id="element-432" data-mp="3j6bbgg6vug7f">Durée souhaitée *</label>
                        <input name="dureeSouhaitee" type="text" required className={inputClass} placeholder="Ex: 1 semaine" data-id="element-433" data-mp="3nymevt0u0k4q" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-434" data-mp="1m96ql622cuuk">
                        <label className={labelClass} data-id="element-435" data-mp="1h9u3ck7a5lrg">
                          Observations particulières (Optionnel)
                        </label>
                        <textarea name="observations" rows={2} className={inputClass} placeholder="Informations complémentaires utiles..." data-id="element-436" data-mp="tl168kex97v9"></textarea>
                      </div>
                    </div>}

                  <Button type="submit" className="w-full text-lg py-6" disabled={isSubmitting} data-id="element-437" data-mp="2exkntjlbvn52">
                    {isSubmitting ? <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-438" data-mp="4l0zya57qs38" />
                        Envoi en cours...
                      </> : 'Envoyer la demande'}
                  </Button>
                </form>}
            </div>
          </div>
        </div>}
    </Layout>;
}